import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";

/**
 * Transcripts store — per-workshop session transcripts captured by paste
 * or file upload. The full text lives on `body` (not a blob) so it's
 * directly full-text searchable. One of the four workshop-record stores
 * built on {@link createWorkshopRecordStore}; same posture as the others
 * (IndexedDB by default; the BFF blob plane when enabled). Transcripts are
 * add/remove-only in the UI — they don't surface the inherited `update()`.
 * Live/auto transcription is out of scope for R1 (see `plans/R2-BACKLOG.md`).
 */

export type Transcript = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  workshopId: string;
  title: string;
  /** Full transcript text — searchable. */
  body: string;
  addedAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 reserved seam). Optional; absent on legacy
   *  records, defaulted to `[]` at every read site. */
  tags?: string[];
  /**
   * Optional attached audio/video for synced playback. All absent on legacy
   * records (text-only transcripts) and on paste/text-file captures.
   *   - `mediaBlobKey` — local IndexedDB key (blob-store) for the media bytes.
   *   - `mediaPath`    — synced path on the BFF/Azure blob plane (self-heal +
   *                      cross-device); `mediaSha` is its delete token ("" on BFF).
   * See {@link import("@/features/ws4-transcript/MediaTranscriptPane")}.
   */
  mediaBlobKey?: string;
  mediaMime?: string;
  mediaKind?: "audio" | "video";
  mediaName?: string;
  mediaPath?: string;
  mediaSha?: string;
};

export const useTranscripts = createWorkshopRecordStore<Transcript>({
  idbKey: "xaes-transcripts",
  recordType: "transcripts",
  // Requires a string body (the searchable payload); see useDecisions for
  // the shared sanitiser rationale.
  sanitiseItem: (t, fallbackWorkshopId) => {
    if (typeof t.id !== "string") return null;
    if (typeof t.title !== "string") return null;
    if (typeof t.body !== "string") return null;
    const str = (v: unknown): string | undefined =>
      typeof v === "string" ? v : undefined;
    return {
      id: t.id,
      workshopId:
        typeof t.workshopId === "string" ? t.workshopId : fallbackWorkshopId,
      title: t.title,
      body: t.body,
      addedAt: typeof t.addedAt === "number" ? t.addedAt : Date.now(),
      addedBy: typeof t.addedBy === "string" ? t.addedBy : "",
      // P0.1 — preserve free-form tags when present; old blobs round-trip
      // cleanly (field stays absent). Array coerced + filtered to strings.
      tags: Array.isArray(t.tags)
        ? t.tags.filter((tag): tag is string => typeof tag === "string")
        : undefined,
      // Optional media fields (same back-compat posture as `tags`): each is
      // string-or-undefined; `mediaKind` is constrained to the two literals so
      // a garbage value can't widen the union. A bad media field drops to
      // undefined but never invalidates an otherwise-valid record.
      mediaBlobKey: str(t.mediaBlobKey),
      mediaMime: str(t.mediaMime),
      mediaKind:
        t.mediaKind === "audio" || t.mediaKind === "video" ? t.mediaKind : undefined,
      mediaName: str(t.mediaName),
      mediaPath: str(t.mediaPath),
      mediaSha: str(t.mediaSha),
    };
  },
});
